import { useEffect, useRef, useState } from 'react';
import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  TextareaHTMLAttributes,
} from 'react';
import { toDisplayScore } from '@cercle/shared';
import styles from './ui.module.scss';

export { styles as ui };

const cx = (...parts: (string | false | undefined)[]): string => parts.filter(Boolean).join(' ');

// ------------------------------------------------------------------ 01 · bouton

type ButtonVariant = 'primary' | 'secondary' | 'dashed' | 'ghost';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  block?: boolean;
}

/** Une seule action pleine par vue — la règle du référentiel. */
export function Button({
  variant = 'primary',
  block = false,
  className,
  type = 'button',
  ...props
}: ButtonProps): ReactNode {
  return (
    <button
      type={type}
      className={cx(
        styles.btn,
        variant === 'secondary' && styles.btnSecondary,
        variant === 'dashed' && styles.btnDashed,
        variant === 'ghost' && styles.btnGhost,
        block && styles.btnBlock,
        className,
      )}
      {...props}
    />
  );
}

// ------------------------------------------------------------------ 02 · tag et badge

export function Tag({
  children,
  selected,
  gold,
  dashed,
  disabled,
  onClick,
}: {
  children: ReactNode;
  selected?: boolean;
  gold?: boolean;
  /** Le filet en pointillé : ce qui n'existe pas encore et qu'on propose de créer. */
  dashed?: boolean;
  disabled?: boolean;
  onClick?: () => void;
}): ReactNode {
  const classes = cx(
    styles.tag,
    gold && styles.tagGold,
    dashed && styles.tagDashed,
    selected && styles.tagSelected,
    !onClick && styles.tagStatic,
  );
  if (!onClick) return <span className={classes}>{children}</span>;
  return (
    <button
      type="button"
      className={classes}
      aria-pressed={selected}
      disabled={disabled}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

/** Or plein sur texte sombre — « Aveugle », « Coup de cœur ». */
export function Badge({ children }: { children: ReactNode }): ReactNode {
  return <span className={styles.badge}>{children}</span>;
}

// ------------------------------------------------------------------ 03 · champ

export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}): ReactNode {
  return (
    <label className={styles.field}>
      <span>{label}</span>
      {children}
      {hint ? <span className={styles.hint}>{hint}</span> : null}
    </label>
  );
}

export function Input({
  pill = false,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & { pill?: boolean }): ReactNode {
  return <input {...props} className={cx(styles.input, pill && styles.inputPill, props.className)} />;
}

export function Textarea(props: TextareaHTMLAttributes<HTMLTextAreaElement>): ReactNode {
  return <textarea {...props} className={cx(styles.textarea, props.className)} />;
}

// ------------------------------------------------------------------ 04 · curseur et jauge

/**
 * Curseur du référentiel : rail de 8 px, remplissage cuivre, pouce doré de 30 px et son halo.
 *
 * L'`input` natif est conservé, transparent, par-dessus le dessin : on garde le clavier, le
 * lecteur d'écran et le comportement tactile du navigateur sans les réimplémenter.
 */
export function Slider({
  value,
  onChange,
  label,
  min = 0,
  max = 100,
  step = 1,
}: {
  value: number;
  onChange: (value: number) => void;
  label: string;
  min?: number;
  max?: number;
  step?: number;
}): ReactNode {
  const percent = ((value - min) / (max - min)) * 100;
  return (
    <div className={styles.slider}>
      <div className={styles.sliderFill} style={{ width: `${percent}%` }} />
      <div className={styles.sliderThumb} style={{ left: `${percent}%` }} />
      <input
        className={styles.sliderInput}
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(event) => onChange(Number(event.target.value))}
        aria-label={label}
        aria-valuetext={toDisplayScore(value)}
      />
    </div>
  );
}

/** Jauge de comparaison — une valeur, pas une action : son dégradé va jusqu'à l'or. */
export function Gauge({ percent }: { percent: number }): ReactNode {
  return (
    <div className={styles.gauge}>
      <div className={styles.gaugeFill} style={{ width: `${Math.max(0, Math.min(100, percent))}%` }} />
    </div>
  );
}

// ------------------------------------------------------------------ 05 · interrupteur

export function Switch({
  checked,
  onChange,
  label,
  gold = false,
  disabled = false,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  /** Or plutôt que cuivre : réservé au mode aveugle, qui est un état de valeur. */
  gold?: boolean;
  disabled?: boolean;
}): ReactNode {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      className={cx(styles.switch, checked && (gold ? styles.switchGold : styles.switchOn))}
      onClick={() => onChange(!checked)}
    />
  );
}

// ------------------------------------------------------------------ 06 · segmenté et pas-à-pas

export function Segmented<T extends string>({
  options,
  value,
  onChange,
  label,
}: {
  options: readonly { value: T; label: string }[];
  value: T | null;
  onChange: (value: T) => void;
  label: string;
}): ReactNode {
  return (
    <div className={styles.segmented} role="group" aria-label={label}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          aria-pressed={value === option.value}
          className={cx(styles.segment, value === option.value && styles.segmentActive)}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

export function Stepper({
  value,
  display,
  onChange,
  label,
  step = 1,
  min = 0,
  max = 100,
}: {
  value: number | null;
  display: string;
  onChange: (value: number) => void;
  label: string;
  step?: number;
  min?: number;
  max?: number;
}): ReactNode {
  const current = value ?? min;
  return (
    <div className={styles.stepper}>
      <button
        type="button"
        className={styles.stepperButton}
        aria-label={`Diminuer ${label}`}
        onClick={() => onChange(Math.max(min, current - step))}
      >
        −
      </button>
      <span className={styles.stepperValue}>{display}</span>
      <button
        type="button"
        className={styles.stepperButton}
        aria-label={`Augmenter ${label}`}
        onClick={() => onChange(Math.min(max, current + step))}
      >
        +
      </button>
    </div>
  );
}

// ------------------------------------------------------------------ 07 · médaillon

export function ScoreMedallion({ score }: { score: number | null }): ReactNode {
  return <span className={styles.medallion}>{score === null ? '—' : toDisplayScore(score)}</span>;
}

export function ScoreDial({ score, label }: { score: number; label: string }): ReactNode {
  return (
    <span className={cx(styles.medallion, styles.medallionLg)}>
      <strong>{toDisplayScore(score)}</strong>
      <small>{label}</small>
    </span>
  );
}

// ------------------------------------------------------------------ 08 · avatar

export function Avatar({
  pseudo,
  url,
  size = 'md',
  guest = false,
}: {
  pseudo: string;
  url?: string | null;
  size?: 'sm' | 'md' | 'lg';
  /** Invité de passage : contour pointillé cuivre, il n'est pas du cercle. */
  guest?: boolean;
}): ReactNode {
  const classes = cx(
    styles.avatar,
    size === 'sm' && styles.avatarSm,
    size === 'lg' && styles.avatarLg,
    guest && styles.avatarGuest,
  );
  if (url) return <img className={classes} src={url} alt="" />;
  return (
    <span className={classes} aria-hidden="true">
      {pseudo.slice(0, 1).toUpperCase()}
    </span>
  );
}

// ------------------------------------------------------------------ 09 · bouteille

export function Bottle({
  size = 'sm',
  muted = false,
}: {
  size?: 'sm' | 'md' | 'lg';
  muted?: boolean;
}): ReactNode {
  return (
    <span
      className={cx(
        styles.bottle,
        size === 'md' && styles.bottleMd,
        size === 'lg' && styles.bottleLg,
        muted && styles.bottleMuted,
      )}
      aria-hidden="true"
    >
      <span />
      <span />
    </span>
  );
}

// ------------------------------------------------------------------ 10 à 12 · états

export function SyncPill({
  state,
  children,
  onClick,
  icon,
  spinning = false,
  label,
}: {
  state: 'pending' | 'done' | 'error';
  children: ReactNode;
  onClick?: () => void;
  icon?: ReactNode;
  /** Fait tourner l'icône le temps de l'action, jamais au-delà. */
  spinning?: boolean;
  /** Ce que fait le clic, pour les lecteurs d'écran : « Sync ✓ » ne le dit pas. */
  label?: string;
}): ReactNode {
  const classes = cx(
    styles.syncPill,
    state === 'done' && styles.syncPillDone,
    state === 'error' && styles.syncPillError,
  );
  const content = (
    <>
      {icon ? (
        <span className={cx(styles.syncPillIcon, spinning && styles.syncPillSpin)}>{icon}</span>
      ) : null}
      {children}
    </>
  );
  if (!onClick) return <span className={classes}>{content}</span>;
  return (
    <button type="button" className={classes} onClick={onClick} aria-label={label}>
      {content}
    </button>
  );
}

/**
 * Le triangle du coup de cœur, replié dans l'angle d'une carte.
 *
 * `justAwarded` fait passer un reflet — une seule fois, à l'attribution du titre. Le
 * référentiel précise « pas de boucle en production » : un reflet qui repasse sans fin
 * finirait par n'attirer l'œil sur rien.
 */
export function Corner({ justAwarded = false }: { justAwarded?: boolean }): ReactNode {
  return (
    <>
      <span className={styles.corner} aria-hidden="true" />
      {justAwarded ? <span className={styles.cornerShine} aria-hidden="true" /> : null}
    </>
  );
}

export function Status({
  kind,
  children,
}: {
  kind: 'active' | 'disabled' | 'ephemeral';
  children: ReactNode;
}): ReactNode {
  return (
    <span
      className={cx(
        styles.status,
        kind === 'active' && styles.statusActive,
        kind === 'disabled' && styles.statusDisabled,
        kind === 'ephemeral' && styles.statusEphemeral,
      )}
    >
      {children}
    </span>
  );
}

// ------------------------------------------------------------------ surfaces

export function Card({
  children,
  gold = false,
  className,
}: {
  children: ReactNode;
  gold?: boolean;
  className?: string;
}): ReactNode {
  return <div className={cx(styles.card, gold && styles.cardGold, className)}>{children}</div>;
}

/** Panneau en dégradé et son halo doré — la carte principale de l'accueil. */
export function Panel({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}): ReactNode {
  return <div className={cx(styles.panel, className)}>{children}</div>;
}

export function Divider(): ReactNode {
  return <hr className={styles.divider} />;
}

export function SheetHandle(): ReactNode {
  return <div className={styles.handle} aria-hidden="true" />;
}

// ------------------------------------------------------------------ divers

export function Kicker({ children }: { children: ReactNode }): ReactNode {
  return <div className={styles.kicker}>{children}</div>;
}

export function Muted({ children }: { children: ReactNode }): ReactNode {
  return <span className={styles.muted}>{children}</span>;
}

export function EmptyState({ children }: { children: ReactNode }): ReactNode {
  return <p className={styles.empty}>{children}</p>;
}

/**
 * Chargement : des silhouettes de ce qui arrive, jamais une roue qui tourne.
 *
 * Le référentiel l'interdit explicitement. `lines` décrit la forme attendue — un titre
 * court puis des cartes — pour que la page ne saute pas quand le contenu se pose.
 */
export function Skeleton({ lines = 3 }: { lines?: number }): ReactNode {
  const heights = ['16px', '56px', '56px', '56px', '56px', '56px'];
  return (
    <div className={styles.skeletons} role="status" aria-label="Chargement">
      {Array.from({ length: lines }, (_, index) => (
        <span
          key={index}
          className={cx(
            styles.skeleton,
            index % 3 === 1 && styles.skeletonDelay1,
            index % 3 === 2 && styles.skeletonDelay2,
          )}
          style={{ height: heights[index] ?? '56px', width: index === 0 ? '60%' : undefined }}
        />
      ))}
    </div>
  );
}

export function ErrorText({ children }: { children: ReactNode }): ReactNode {
  return children ? <p className={styles.error}>{children}</p> : null;
}

/**
 * Un texte qu'on copie — un lien d'invitation, aujourd'hui.
 *
 * Le champ reste visible et sélectionnable plutôt que caché derrière le seul bouton :
 * `navigator.clipboard` n'existe pas hors contexte sécurisé, et l'application ouverte depuis
 * une adresse de réseau local en `http://` en est un cas réel. Le bouton se replie alors sur
 * une sélection du contenu, qu'un `Ctrl+C` achève. Un bouton qui échouerait en silence
 * serait pire que pas de bouton du tout.
 */
export function CopyField({ value, label }: { value: string; label: string }): ReactNode {
  const inputRef = useRef<HTMLInputElement>(null);
  const [copied, setCopied] = useState(false);

  // Le minuteur est annulé au démontage : sans cela, fermer l'encart pendant les deux
  // secondes déclencherait une mise à jour d'état sur un composant disparu.
  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  async function copy(): Promise<void> {
    inputRef.current?.select();
    try {
      await navigator.clipboard?.writeText(value);
      setCopied(true);
    } catch {
      // Refus du navigateur : le champ est déjà sélectionné, la copie manuelle reste à
      // un raccourci. On ne prétend pas avoir copié.
      setCopied(false);
    }
  }

  return (
    <span className={styles.copyField}>
      <input ref={inputRef} className={styles.input} value={value} readOnly aria-label={label} />
      <Button variant="secondary" onClick={() => void copy()}>
        {copied ? 'Copié ✓' : 'Copier'}
      </Button>
    </span>
  );
}
